/** @jsx isolateComponent */

import { ReactNode } from 'react';

import { Link } from '../../navigation/Link';
import { Animation, ColorValue, Colors, Typography } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

const hexToRrggbbaa = (color: string, opacity: number): string => {
  const opacityHex = Math.round(opacity * 255).toString(16);
  const prefixedOpacityHex =
    opacityHex.length > 1 ? opacityHex : `0${opacityHex}`;
  return `${color}${prefixedOpacityHex}`;
};

type BadgeColorVariant = {
  base: {
    background: string;
    color: ColorValue;
  };
  hover: {
    background: string;
    color: ColorValue;
  };
};
const BadgeVariants = {
  light: {
    base: {
      background: hexToRrggbbaa(Colors.black, 0.05),
      color: Colors.black,
    },
    hover: {
      background: hexToRrggbbaa(Colors.blue100, 1),
      color: Colors.blue500,
    },
  },
  white: {
    base: {
      background: hexToRrggbbaa(Colors.white, 1),
      color: Colors.black,
    },
    hover: {
      background: hexToRrggbbaa(Colors.black, 0.05),
      color: Colors.blue500,
    },
  },
  grey: {
    base: {
      background: hexToRrggbbaa(Colors.black, 0.1),
      color: Colors.grey700,
    },
    hover: {
      background: hexToRrggbbaa(Colors.black, 0.1),
      color: Colors.grey700,
    },
  },
  dark: {
    base: {
      background: hexToRrggbbaa(Colors.white, 0.3),
      color: Colors.white,
    },
    hover: {
      background: hexToRrggbbaa(Colors.white, 0.2),
      color: Colors.white,
    },
  },
};

type Props = {
  /** Indicates the element represents the current item within a list. */
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | true | false;
  /** Text to render inside the badge. */
  children: ReactNode;
  /**
   * The color variant of the badge.
   *
   * @default 'light'
   * */
  color?: keyof typeof BadgeVariants;
  /** Overrides the Badge's color variant. Supercedes the `color` prop. */
  colorVariant?: BadgeColorVariant;
  /** Make badge clickable with link. */
  href?: string;
  /** Anchor link target. */
  hrefTarget?: '_self' | '_blank' | '_parent' | '_top';
  /** Optional click handler */
  onClick?: React.MouseEventHandler;
  /**
   *  The size of the badge to render.
   *
   * @default 'small'
   * */
  size?: 'small' | 'medium' | 'large';
};

const defaultProps = {
  color: 'light' as keyof typeof BadgeVariants,
  size: 'small' as Props['size'] & string,
};

type PropsWithDefaults = Props & typeof defaultProps;

const Styles = labelStyles('Badge', {
  root: (props: PropsWithDefaults, badgeColorVariant: BadgeColorVariant) => [
    {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative' as 'relative',
      zIndex: 1,
      borderRadius: 100,
    },

    withModifier(
      props.size,
      {
        small: {
          ...Typography.altText900,
          fontWeight: 600,
          height: 30,
          padding: '0 15px',
        },
        medium: {
          ...Typography.subtitle800,
          fontWeight: 600,
          height: 40,
          padding: '0 20px',
        },
        large: {
          ...Typography.subtitle900,
          fontWeight: 700,
          height: 60,
          padding: '0 30px',
        },
      }[props.size]
    ),

    withModifier(props.colorVariant ? 'custom-color' : props.color, {
      background: badgeColorVariant.base.background,
      color: badgeColorVariant.base.color,
    }),

    (props.href || props.onClick) &&
      withModifier('as-link', {
        cursor: 'pointer',
        '&:hover, &:focus': {
          color: badgeColorVariant.hover.color,
        },
        '&:hover::before, &:focus::before': {
          opacity: 1,
        },

        /**
         * Linear-gradient backgrounds cannot be animated with `transition` natively
         * See: https://keithjgrant.com/posts/2017/07/transitioning-gradients/
         * This implementation taken from the above uses a pseudo-element to transition
         * the opacity property to simulate animating a linear-gradient. Still works fine
         * with non-linear-gradient backgrounds as well.
         */
        '&::before': {
          position: 'absolute' as 'absolute',
          content: '""',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: badgeColorVariant.hover.background,
          zIndex: -1,
          transition: Animation.transitionValue,
          opacity: 0,
          borderRadius: 20,
        },
      }),
  ],
});

/**
 * Renders a badge component. Used for breadcrumbs, etc.
 */
export const Badge = (_props: Props) => {
  const props: PropsWithDefaults = {
    ...defaultProps,
    ..._props,
  };
  const badgeVariant = props.colorVariant || BadgeVariants[props.color];
  const Component = props.href ? Link : props.onClick ? 'button' : 'span';
  const isLink = Component === Link;
  const { ariaCurrent } = props;
  const ariaProps = isLink ? { ariaCurrent } : { 'aria-current': ariaCurrent };
  return (
    <Component
      {...ariaProps}
      css={Styles.root(props, badgeVariant)}
      href={props.href}
      onClick={props.onClick}
      target={props.hrefTarget}
    >
      {props.children}
    </Component>
  );
};

Badge.defaultProps = defaultProps;
